import clsx from 'clsx'
import React from 'react'
import { observer } from 'mobx-react-lite'
import { CustomTableStore, useCustomTableStore } from '../CustomTableStore'
import TableCell from '@mui/material/TableCell'
import { stylesRowOpenedNoBorder } from './shared-styles'
import { CustomTableLocalRow } from '../CustomTableLocalRow'
import { SplitButton } from '../../split-button'
import useCustomTableRenderLog from '../hooks/useCustomTableRenderLog'
import useStore from 'reusable/mobx-store-utils'
import { notNull } from '@gap/utils/utils'
import { isArray } from 'reusable/utils/array-utils'
import useClasses from 'theme/UseClasses'

interface CustomTableRowActionCellProps<RowData extends AnyObject, RowStore> {
   localRow: CustomTableLocalRow<RowData, RowStore>
}
const theme = {
   spacing(val: number) {
      return val * 8
   },
}

const styles = {
   actionCell: {
      width: '1px',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingTop: 0,
      paddingBottom: 0,
   }
}

export const CustomTableRowActionCell = observer(function CustomTableRowActionCell
   <RowData extends AnyObject, RowStore>(
      props: CustomTableRowActionCellProps<RowData, RowStore>
   ) {
   useCustomTableRenderLog('CustomTableRowActionCell')
   const sharedClasses = useClasses(stylesRowOpenedNoBorder)
   const classes = useClasses(styles)

   const customTableStore = useCustomTableStore<CustomTableStore<RowData, RowStore>>()

   const localStore = useStore(
      sp => ({
         get hasRowActions() {
            return sp.actions.length > 0
         },
         get actionCellContent() {
            const options = []
            if (sp.actions) {
               const actions = isArray(sp.actions) ? sp.actions : sp.actions(sp.localRow.row)
               for (const action of actions) {
                  if (!!action) {
                     options.push({
                        title: action.title || '',
                        callback: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                           notNull(action).callback(sp.localRow.row)
                           event.stopPropagation()
                        },
                        hasPriority: action.hasPriority,
                        disabled: action.disabled,
                     })
                  }
               }
            }
            if (options.length > 0) {
               return <SplitButton options={options} size='small' />
            }
            return null
         },
         get classes() {
            return clsx(
               sp.localRow.collapsibleOpen ? sharedClasses.cellOpened : sharedClasses.cellClosed,
               classes.actionCell
            )
         },
      }),
      { actions: customTableStore.rowActions, ...props }
   )

   if (!localStore.hasRowActions) {
      return null
   }
   return <TableCell className={localStore.classes}>{localStore.actionCellContent}</TableCell>
}
)
